import { UserServiceStatus, WbAppType, checkAxiosResponse } from '@mst-fe/shared';
import axios from 'axios';

import { LONG_REQUEST_TIMEOUT } from '../../constants';
import { observeEndpoint } from '../../services/endpoint-observer';
import { axiosOptions } from '../../services/requests';

export interface UserService {
  id: string;
  name: string;
  url: string | undefined;
  status: UserServiceStatus;
  type: WbAppType;
}

export interface UserChart {
  id: string;
  name: string;
  url: string;
}

export interface DashboardData {
  id: string;
  name: string;
}

export interface DashboardDetailWidgetPosition {
  colStart: number;
  nCols: number;
  rowStart: number;
  nRows: number;
}

export interface DashboardDetailWidgetLayout extends DashboardDetailWidgetPosition {
  id: string;
}

export interface DashboardDetailWidgetData extends DashboardDetailWidgetPosition {
  id: string;
  name: string | null | undefined;
  serviceId: string | null | undefined;
  serviceName: string | null | undefined;
  serviceStatus: UserServiceStatus | null | undefined;
  serviceUrl: string | undefined;
  serviceType: 'SUPERSET' | 'MODULE' | 'SERVICE' | null | undefined;
}

export interface DashboardDetailData {
  id: string;
  name: string;
  widgets: DashboardDetailWidgetData[];
}

export interface CreateDashboardRequest {
  name: string;
}

export interface UpdateDashboardRequest {
  name?: string;
}

export interface CreateDashboardWidgetRequest {
  id: string;
  colStart: number;
  nCols: number;
  rowStart: number;
  nRows: number;
}

export interface UpdateDashboardWidgetRequest {
  name?: string | null;
  colStart?: number;
  nCols?: number;
  rowStart?: number;
  nRows?: number;
  serviceId?: string;
  serviceType?: 'SUPERSET' | 'MODULE' | 'SERVICE';
}

export class DashboardApi {
  async getUserServices(): Promise<UserService[] | undefined> {
    return await checkAxiosResponse(axios.get<UserService[]>(`/api/zoe/dashboard/user-services`, axiosOptions()));
  }

  async getUserCharts(): Promise<UserChart[] | undefined> {
    return await checkAxiosResponse(axios.get<UserChart[]>(`/api/zoe/dashboard/user-charts`, axiosOptions()));
  }

  async getDashboards() {
    return await checkAxiosResponse(axios.get<DashboardData[]>('/api/zoe/dashboard/', axiosOptions()));
  }

  getDashboardsSSE(cb: (value: DashboardData[]) => void) {
    return observeEndpoint<DashboardData[]>(`/api/zoe/dashboard/sse`, cb);
  }

  async getDashboardDetail(id: string) {
    return await checkAxiosResponse(axios.get<DashboardDetailData>(`/api/zoe/dashboard/${id}`, axiosOptions(LONG_REQUEST_TIMEOUT)));
  }

  getDashboardDetailSSE(id: string, cb: (value: DashboardDetailData) => void) {
    return observeEndpoint<DashboardDetailData>(`/api/zoe/dashboard/${id}/sse`, cb);
  }

  async createDashboard(data: CreateDashboardRequest): Promise<{ ok: boolean } | undefined> {
    return await checkAxiosResponse(axios.post<{ ok: boolean }>(`/api/zoe/dashboard/`, data, axiosOptions()));
  }

  async updateDashboard(id: string, data: UpdateDashboardRequest): Promise<{ ok: boolean } | undefined> {
    return await checkAxiosResponse(axios.put<{ ok: boolean }>(`/api/zoe/dashboard/${id}`, data, axiosOptions()));
  }

  async deleteDashboard(id: string): Promise<{ ok: boolean } | undefined> {
    return await checkAxiosResponse(axios.delete<{ ok: boolean }>(`/api/zoe/dashboard/${id}`, axiosOptions()));
  }

  async createWidget(dashboardId: string, data: CreateDashboardWidgetRequest): Promise<{ ok: boolean } | undefined> {
    return await checkAxiosResponse(axios.post<{ ok: boolean }>(`/api/zoe/dashboard/${dashboardId}/widget`, data, axiosOptions()));
  }

  async updateWidget(dashboardId: string, id: string, data: UpdateDashboardWidgetRequest): Promise<{ ok: boolean } | undefined> {
    return await checkAxiosResponse(axios.put<{ ok: boolean }>(`/api/zoe/dashboard/${dashboardId}/widget/${id}`, data, axiosOptions()));
  }

  async deleteWidget(dashboardId: string, id: string): Promise<{ ok: boolean } | undefined> {
    return await checkAxiosResponse(axios.delete<{ ok: boolean }>(`/api/zoe/dashboard/${dashboardId}/widget/${id}`, axiosOptions()));
  }
}
